import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type OpenSpotlightOptions = {
  query?: string;
};

type OpenHandler = (opts?: OpenSpotlightOptions) => void;

type SpotlightHostContextValue = {
  openSpotlight: (opts?: OpenSpotlightOptions) => void;
  /** GlobalSearch registers the real opener; returns unregister. */
  registerOpenHandler: (handler: OpenHandler) => () => void;
  isSpotlightOpen: boolean;
  setSpotlightOpen: (open: boolean) => void;
  canOpenSpotlight: boolean;
  setCanOpenSpotlight: (can: boolean) => void;
};

const SpotlightHostContext = createContext<SpotlightHostContextValue | null>(null);

/**
 * Hosts Spotlight open API for GlobalSearch (⌘K) and keyboard shortcuts.
 */
export const SpotlightHostProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const handlerRef = useRef<OpenHandler>(() => {});
  const [isSpotlightOpen, setSpotlightOpen] = useState(false);
  const [canOpenSpotlight, setCanOpenSpotlight] = useState(false);

  const openSpotlight = useCallback((opts?: OpenSpotlightOptions) => {
    handlerRef.current(opts);
  }, []);

  const registerOpenHandler = useCallback((handler: OpenHandler) => {
    handlerRef.current = handler;
    return () => {
      handlerRef.current = () => {};
    };
  }, []);

  const value = useMemo(
    () => ({
      openSpotlight,
      registerOpenHandler,
      isSpotlightOpen,
      setSpotlightOpen,
      canOpenSpotlight,
      setCanOpenSpotlight,
    }),
    [
      openSpotlight,
      registerOpenHandler,
      isSpotlightOpen,
      canOpenSpotlight,
    ],
  );

  return (
    <SpotlightHostContext.Provider value={value}>{children}</SpotlightHostContext.Provider>
  );
};

export const useSpotlightHost = (): SpotlightHostContextValue => {
  const ctx = useContext(SpotlightHostContext);
  if (!ctx) {
    throw new Error('useSpotlightHost must be used within SpotlightHostProvider');
  }
  return ctx;
};
